import { useEffect } from 'react';
import css from 'styled-jsx/css';
import { useReduxState } from 'hooks';
import { Link } from 'components/common';
import { CheckboxInput } from 'components/common/form/common';

import BoxTableCell from './BoxTableCell';
import { useBoxContext, useBoxDispatch } from './BoxContext';

import type { FormEvent } from 'react';

interface Props {
  as?: string;
  children: React.ReactNode;
  hideCheckBox?: boolean;
  href?: string;
  labelFor?: string;
  onClick?: (ev: React.KeyboardEvent | React.MouseEvent) => void;
  withCheckboxValue?: number | string;
}

const BoxTableRow = ({
  as,
  children,
  hideCheckBox = false,
  href,
  labelFor,
  onClick,
  withCheckboxValue,
  ...attrs
}: Props) => {
  const state = useBoxContext();

  const dispatch = useBoxDispatch();

  const { theme } = useReduxState();

  useEffect(() => {
    if (!hideCheckBox) {
      dispatch({ type: 'setVisibleItem', itemId: withCheckboxValue });
    }
  }, []);

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.target !== ev.currentTarget) return;
    // check if enter or spacebar
    if (ev.keyCode === 13 || ev.keyCode === 32) {
      ev.preventDefault();
      onClick(ev);
    }
  };

  const handleSelect = (ev: FormEvent<HTMLElement>) => {
    const value = (ev.target as HTMLInputElement).checked;
    dispatch({
      type: 'changeSelection',
      itemId: withCheckboxValue,
      itemValue: value
    });
  };

  if (state?.checkboxIsActive && !withCheckboxValue && !hideCheckBox) {
    throw new Error(
      'Checkbox value is active inside this table, but withCheckboxValue prop is missing in BoxTableRow'
    );
  }

  if (href) {
    const { className, styles } = css.resolve`
      a {
        color: ${theme.textColorLight};
        cursor: pointer;
        display: table-row;
        font-family: inherit;
        font-size: 1.5rem;
        font-weight: 600;
        outline: 0;
        transition: background-color 0.3s;
      }
      a:hover {
        background-color: ${theme.lightGreyLighter};
        text-decoration: none;
      }
      a:focus {
        box-shadow: 0 0 0 5px rgba(0, 0, 0, 0.15);
        text-decoration: none;
      }
      a ~ a {
        border-top: 1px solid ${theme.lightGrey};
      }
    `;

    return (
      <Link as={as} href={href} className={className} {...attrs}>
        {withCheckboxValue !== undefined ? (
          <BoxTableCell
            sticky={true}
            stickyBackgroundColor="white"
            stickyOffset={-48}
            shrink={true}
          >
            <CheckboxInput
              id={`select-row-in-box-${state.checkboxIdentifier}-${withCheckboxValue}`}
              onChange={handleSelect}
              stopPropagation={true}
              {...{
                checked: state.selectedItems?.[withCheckboxValue] || false
              }}
            />
          </BoxTableCell>
        ) : null}

        {children}

        {styles}
      </Link>
    );
  }

  const WrapperEl = labelFor ? 'label' : 'div';

  return (
    <WrapperEl
      className={
        `box-table-row focus-box-shadow` +
        `${onClick || labelFor ? ' box-table-row-button' : ''}`
      }
      {...(onClick
        ? {
            onClick: onClick,
            onKeyDown: handleKeyDown,
            role: 'button',
            tabIndex: 0
          }
        : {})}
      {...(labelFor ? { htmlFor: labelFor } : {})}
      {...attrs}
    >
      {withCheckboxValue !== undefined ? (
        <BoxTableCell
          sticky={true}
          stickyBackgroundColor="white"
          stickyOffset={-48}
          shrink={true}
        >
          {!hideCheckBox && (
            <CheckboxInput
              id={`select-row-in-box-${state.checkboxIdentifier}-${withCheckboxValue}`}
              onChange={handleSelect}
              stopPropagation={true}
              {...{
                checked: state.selectedItems?.[withCheckboxValue] || false
              }}
            />
          )}
        </BoxTableCell>
      ) : null}

      {children}

      <style jsx>{`
        .box-table-row {
          color: ${theme.textColorLight};
          display: table-row;
          font-size: 1.5rem;
          font-weight: 600;
        }
        .box-table-row ~ .box-table-row {
          border-top: 1px solid ${theme.lightGrey};
        }
        .box-table-row-button {
          cursor: pointer;
          font-family: inherit;
          outline: 0;
          text-decoration: none;
          transition: background-color 0.3s;
        }
        .box-table-row-button:hover {
          background-color: ${theme.lightGreyLighter};
        }
        .box-table-row-button:focus {
          box-shadow: 0 0 0 5px rgba(0, 0, 0, 0.15);
        }
        .box-table-row :global(.box-table-cell) {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
        }
      `}</style>
    </WrapperEl>
  );
};

export default BoxTableRow;
